import type { KnowledgeDocument } from '@ralph/contracts'
import { SURFACE_LABEL } from '@ralph/contracts'
import {
  CircleCheck,
  CircleDashed,
  ExternalLink,
  FileText,
  Images,
  RefreshCw,
  ShieldAlert,
  TriangleAlert,
  X
} from 'lucide-react'
import { useRef, useState } from 'react'
import { useFocusTrap } from '@/components/ui/focus-trap.ts'
import { Overlay } from '@/components/ui/Overlay.tsx'
import { Button, EmptyState, IconButton, Input, Readout, Spinner } from '@/components/ui/primitives.tsx'
import { useRefetchItem } from '@/hooks/use-connectors.ts'
import { useDocumentChunks, useReprocessDocument } from '@/hooks/use-knowledge-bases.ts'
import { foldForSearch } from '@/i18n/format.ts'
import { describeApiError, useI18n } from '@/i18n/index.ts'
import { diagnoseFailure, standingOf } from './knowledge-console.ts'
import { ProviderMark } from './ProviderMark.tsx'

/**
 * One document, opened up (PRD-07 KB-019).
 *
 * The console could count passages and never show one. A maintainer diagnosing "why did it answer
 * that" or "why does this 113-page manual never come back" has two questions the row cannot
 * answer — what the parser actually extracted, and how the chunker cut it — and both were
 * reachable only through the database. The endpoint that lists a document's passages had been
 * implemented and wired to nothing since the console was written.
 *
 * The passages shown here are the same rows retrieval scores and the same text the model is
 * handed. That is the whole point: not a preview of the file, but the corpus as the machine
 * holds it. A chunk that is empty, a heading the parser invented, a table flattened into
 * gibberish — each is visible here and nowhere else in the product.
 *
 * ── Lineage, not trivia ──
 *
 * `parserVersion`, `embeddingModel` and `contentHash` were already on every row the table
 * fetched and were thrown away on arrival. They are what makes a citation auditable (KB-003):
 * when a retrieval regression is traced to a parser upgrade, this strip is where a maintainer
 * confirms which version produced the passages they are looking at. Their VALUES are identifiers
 * and never translate (PRD-27 D-17); only the labels beside them do.
 *
 * ── The failure is where the work is ──
 *
 * A failed document is the one row on this screen somebody has to act on, so it gets the top
 * of the panel: the server's own sentence in full — never clamped here, this is the surface
 * that has room for it — and, when the cause is one of the handful worth naming, what to do
 * next. Ingestion does not keep the original bytes (`knowledge_documents.storage_path` is
 * never written), so the honest remedy is always a fresh upload, and the drawer says so
 * rather than offering a Retry that would re-read a file the server no longer has.
 *
 * ── Where the words are ──
 *
 * All of them are in `i18n/messages/<locale>/knowledge.ts` under `drawer` (PRD-27 LOC-010). This
 * panel was one of the nine surfaces that never got a catalogue (F-14) and held around seventy-
 * eight English literals, plural forms included; every one of those forms is now a catalogue
 * entry asking `Intl.PluralRules`, because Brazilian Portuguese puts ZERO in the singular
 * category and an `n === 1` check is wrong in one of the two shipped languages on every empty
 * count. Every read is inside a component body — never at module scope (F-3).
 */

/** Rendered at once. A thousand passages of full text is a scroll nobody finishes and a frame nobody paints. */
const PAGE = 120

/** The endpoint stops here, and the note under the list says so rather than ending silently. */
const ENDPOINT_LIMIT = 1000

export const DocumentDrawer = ({
  document,
  canManage,
  onClose,
  onRemove
}: {
  document: KnowledgeDocument
  canManage: boolean
  onClose: () => void
  onRemove: () => void
}) => {
  const messages = useI18n().t
  const t = messages.knowledge
  const panelRef = useRef<HTMLDivElement>(null)
  useFocusTrap(panelRef, { onEscape: onClose })

  return (
    <Overlay>
      <div
        className="fade-in fixed inset-0 z-50 flex justify-end"
        role="dialog"
        aria-modal="true"
        aria-label={t.drawer.label(document.title)}
      >
        <button
          type="button"
          aria-label={t.drawer.closeScrim}
          onClick={onClose}
          className="flex-1 bg-[var(--scrim)]/25 backdrop-blur-[1px]"
        />
        <div
          ref={panelRef}
          tabIndex={-1}
          className="m-3 flex w-[580px] max-w-[94vw] flex-col rounded-panel border border-rule bg-card-solid shadow-frame focus:outline-none"
        >
          <header className="flex items-start justify-between gap-3 border-b border-rule px-4 py-3.5">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <FileText size={14} className="shrink-0 text-grounded" aria-hidden />
                <h2 className="truncate text-[15px]">{document.title}</h2>
              </div>
              <p className="readout mt-1 truncate text-fg-subtle">{document.filename}</p>
            </div>
            <IconButton label={messages.common.close} size={29} onClick={onClose}>
              <X size={14} aria-hidden />
            </IconButton>
          </header>

          <div className="min-h-0 flex-1 overflow-y-auto">
            <StandingBlock document={document} />
            {document.origin && <Origin document={document} />}
            <Lineage document={document} />
            <ReadingReport document={document} />
            <Passages document={document} />
          </div>

          {canManage && (
            <footer className="flex items-center justify-between gap-3 border-t border-rule px-4 py-3">
              <p className="text-[12px] leading-[1.4] text-fg-subtle">
                {document.origin?.folderId
                  ? messages.knowledgeFolders.document.removeFooter(document.origin.path)
                  : t.drawer.removeFooter}
              </p>
              <span className="flex items-center gap-1.5">
                {document.origin?.itemId ? (
                  <FetchAgainButton itemId={document.origin.itemId} />
                ) : (
                  <ReadAgainButton document={document} />
                )}
                <RemoveButton
                  title={document.title}
                  onConfirm={onRemove}
                  leaveOut={Boolean(document.origin?.folderId)}
                />
              </span>
            </footer>
          )}
        </div>
      </div>
    </Overlay>
  )
}

/**
 * Two clicks, in place, no dialog.
 *
 * Deleting an indexed document is not undoable from this console and used to be one
 * unguarded click on a bin icon at the end of a row — the same gesture as scrolling past it.
 * A second, differently-worded click is the smallest thing that makes the action deliberate,
 * and it stays inside the row rather than throwing a modal over the table the maintainer is
 * still reading.
 */
export const RemoveButton = ({
  title,
  onConfirm,
  leaveOut = false
}: {
  title: string
  onConfirm: () => void
  /** A synced document: removing also leaves it out of its folder, and the button says so (PRD-25 D-6). */
  leaveOut?: boolean
}) => {
  const messages = useI18n().t
  const t = messages.knowledge
  const [armed, setArmed] = useState(false)

  if (!armed) {
    return (
      <Button size="sm" variant="ghost" aria-label={t.documents.remove(title)} onClick={() => setArmed(true)}>
        {messages.common.remove}
      </Button>
    )
  }

  const confirmWords = leaveOut ? messages.knowledgeFolders.document.removeAndLeaveOut : t.documents.removeForGood

  return (
    <span className="flex items-center gap-1.5">
      <Button
        size="sm"
        variant="danger"
        autoFocus
        // The accessible name carries the visible words, so what a reader hears is what is on the button.
        aria-label={t.drawer.actionOn(confirmWords, title)}
        onClick={() => {
          setArmed(false)
          onConfirm()
        }}
      >
        {confirmWords}
      </Button>
      <Button size="sm" variant="ghost" onClick={() => setArmed(false)}>
        {t.documents.keep}
      </Button>
    </span>
  )
}

const StandingBlock = ({ document }: { document: KnowledgeDocument }) => {
  // Bound in the body, never captured at module scope (PRD-27 F-3).
  const fmt = useI18n()
  const t = fmt.t.knowledge
  const standing = standingOf(document)

  if (standing === 'answering') {
    return (
      <div className="flex items-center gap-2 border-b border-rule px-4 py-2.5">
        <CircleCheck size={13} strokeWidth={2.4} className="shrink-0 text-grounded" aria-hidden />
        <p className="text-[12.5px] leading-relaxed text-fg-muted">{t.drawer.answering(fmt, document.chunkCount)}</p>
      </div>
    )
  }

  if (standing === 'working') {
    return (
      <div className="flex items-center gap-2 border-b border-rule px-4 py-2.5">
        <Spinner size={12} />
        <p className="text-[12.5px] leading-relaxed text-fg-muted">{t.drawer.beingRead(t.status[document.status])}</p>
      </div>
    )
  }

  if (standing === 'silent') {
    return (
      <div className="border-b border-rule bg-alert-soft px-4 py-3">
        <p className="flex items-start gap-2 text-[12.5px] leading-relaxed text-fg-muted">
          <CircleDashed size={13} strokeWidth={2.2} className="mt-[3px] shrink-0 text-alert" aria-hidden />
          <span>
            <span className="block font-semibold text-fg">{t.drawer.silentTitle}</span>
            {t.drawer.silentBody}
          </span>
        </p>
      </div>
    )
  }

  if (standing === 'quarantined') {
    return (
      <div className="border-b border-rule bg-alert-soft px-4 py-3">
        <p className="flex items-start gap-2 text-[12.5px] leading-relaxed text-fg-muted">
          <ShieldAlert size={13} strokeWidth={2.2} className="mt-[3px] shrink-0 text-alert" aria-hidden />
          <span>
            <span className="block font-semibold text-fg">{t.drawer.quarantinedTitle}</span>
            {t.drawer.quarantinedBody}
          </span>
        </p>
        {(document.quarantineReason ?? document.statusDetail) && (
          <ServerDetail detail={document.quarantineReason ?? document.statusDetail ?? ''} />
        )}
      </div>
    )
  }

  const diagnosis = diagnoseFailure(document.statusDetail)

  return (
    <div className="border-b border-rule bg-alert-soft px-4 py-3">
      <p className="flex items-start gap-2 text-[12.5px] leading-relaxed text-fg-muted">
        <TriangleAlert size={13} strokeWidth={2.2} className="mt-[3px] shrink-0 text-alert" aria-hidden />
        <span>
          <span className="block font-semibold text-fg">{t.documents.couldNotRead}</span>
          {/* Always the remedy first when there is one: the server's sentence is the record,
              but it is not instructions. What arrives from `diagnoseFailure` is a KEY, looked up
              in the catalogue — never a sentence chosen by matching the server's (PRD-27 D-6). */}
          {diagnosis.remedy ? t.documents.remedy[diagnosis.remedy] : t.documents.noRemedy}
        </span>
      </p>
      {document.statusDetail && <ServerDetail detail={document.statusDetail} />}
      <p className="readout mt-2.5 text-fg-subtle">{t.drawer.fileKept}</p>
    </div>
  )
}

/*
 * The server's sentence, whole.
 *
 * In the table it is clamped, because there it shares a row with five columns a maintainer has
 * to keep reading. Here it has the panel to itself and a scroll of its own, so a kilobyte of
 * driver output is legible rather than either truncated or destructive — and it is the one
 * place in the product where the raw text is guaranteed to be complete.
 */
const ServerDetail = ({ detail }: { detail: string }) => (
  <div className="mt-2.5 max-h-32 overflow-y-auto rounded-tile border border-rule bg-card-solid px-2.5 py-2">
    <p className="readout whitespace-pre-wrap break-words leading-relaxed text-alert">{detail}</p>
  </div>
)

/**
 * Read the stored copy again with the pipeline now deployed (PRD-24 ING-013).
 *
 * One click, no confirmation: nothing is destroyed, the passages are replaced wholesale in
 * one transaction, and a maintainer reaching for this is already looking at the reason — an
 * outdated stamp, a scan that failed before OCR was turned on, a reader upgrade to evaluate.
 */
const ReadAgainButton = ({ document }: { document: KnowledgeDocument }) => {
  const messages = useI18n().t
  const t = messages.knowledge
  const reprocess = useReprocessDocument(document.sourceId)
  const busy = reprocess.isPending || standingOf(document) === 'working'
  // A quarantined copy re-quarantines: the block above says there is no way to read it as
  // it is, and a button that contradicts that sentence is worse than none.
  if (document.status === 'quarantined') return null
  return (
    <span className="flex items-center gap-1.5">
      <Button
        size="sm"
        variant="ghost"
        icon={<RefreshCw size={12} aria-hidden />}
        disabled={busy}
        onClick={() => reprocess.mutate(document.id)}
      >
        {busy ? t.drawer.reading : t.drawer.readAgain}
        {/* The visible label stays the accessible name's prefix (WCAG 2.5.3); the title is
            appended for a listener who cannot see which drawer is open. */}
        <span className="sr-only"> — {document.title}</span>
      </Button>
      {reprocess.isError && (
        <span role="alert" className="readout text-alert">
          {describeApiError(messages, reprocess.error)}
        </span>
      )}
    </span>
  )
}

/**
 * What was read, and what was left out (PRD-24 ING-014).
 *
 * The counts a maintainer cannot get from the passages: how many pictures the file held and
 * how many were actually read, why the rest were skipped, whether hidden slides or sheets
 * were passed over, and the warnings the reader recorded. It is the difference between "the
 * deck indexed" and "the deck indexed, minus the two hidden slides and the fourteen icons".
 */
const ReadingReport = ({ document }: { document: KnowledgeDocument }) => {
  const fmt = useI18n()
  const t = fmt.t.knowledge
  const report = document.report
  if (!report) return null

  const { images, blocks, hidden, warnings } = report
  const skipped: string[] = []
  if (images.skipped.tiny > 0) skipped.push(t.drawer.skipped.tiny(fmt, images.skipped.tiny))
  if (images.skipped.duplicate > 0) skipped.push(t.drawer.skipped.duplicate(fmt, images.skipped.duplicate))
  if (images.skipped.unsupported > 0) skipped.push(t.drawer.skipped.unsupported(fmt, images.skipped.unsupported))
  if (images.skipped.off > 0) skipped.push(t.drawer.skipped.off(fmt, images.skipped.off))
  if (images.skipped.unavailable > 0) skipped.push(t.drawer.skipped.unavailable(fmt, images.skipped.unavailable))
  if (images.skipped.cap > 0) skipped.push(t.drawer.skipped.cap(fmt, images.skipped.cap))

  const structure: string[] = []
  if (blocks.headings > 0) structure.push(t.drawer.structure.headings(fmt, blocks.headings))
  if (blocks.tables > 0) structure.push(t.drawer.structure.tables(fmt, blocks.tables))
  if (blocks.listItems > 0) structure.push(t.drawer.structure.listItems(fmt, blocks.listItems))
  if (blocks.notes > 0) structure.push(t.drawer.structure.notes(fmt, blocks.notes))
  if (report.pages !== undefined && report.pages > 0) structure.push(t.drawer.structure.pages(fmt, report.pages))
  if (report.slides !== undefined && report.slides > 0) structure.push(t.drawer.structure.slides(fmt, report.slides))
  if (report.sheets !== undefined && report.sheets.length > 0)
    structure.push(t.drawer.structure.sheets(fmt, report.sheets.length))

  return (
    <section className="border-b border-rule px-4 py-3.5">
      <h3 className="eyebrow mb-2.5 flex items-center gap-1.5">
        <Images size={11} strokeWidth={2.2} aria-hidden />
        {t.drawer.whatWasRead}
      </h3>
      <div className="grid grid-cols-2 gap-x-5 gap-y-2 sm:grid-cols-3">
        <Readout
          label={t.drawer.readouts.structure}
          value={structure.length > 0 ? t.drawer.joinParts(structure) : t.drawer.proseOnly}
        />
        <Readout
          label={t.drawer.readouts.pictures}
          value={
            images.found === 0
              ? t.drawer.noPictures
              : t.drawer.pictures(
                  fmt.formatCount(images.found),
                  fmt.formatCount(images.withText),
                  // A fraction through `Intl`, so the sign sits where the locale puts it.
                  images.meanConfidence === null ? null : fmt.formatPercent(images.meanConfidence / 100, 0)
                )
          }
        />
        {report.ocr && (
          <Readout
            label={t.drawer.readouts.ocr}
            value={t.drawer.ocrValue(
              report.ocr.engine,
              report.ocr.languages.join('+'),
              report.ocr.pagesScanned > 0 ? t.drawer.scannedPages(fmt, report.ocr.pagesScanned) : null
            )}
          />
        )}
        {(hidden.slides > 0 || hidden.sheets > 0) && (
          <Readout
            label={t.drawer.readouts.notRead}
            value={t.drawer.joinParts(
              [
                hidden.slides > 0 ? t.drawer.hiddenSlides(fmt, hidden.slides) : '',
                hidden.sheets > 0 ? t.drawer.hiddenSheets(fmt, hidden.sheets) : ''
              ].filter(Boolean)
            )}
          />
        )}
        {/* `formatSeconds`, not `formatDuration`: this column is compared by eye down the page,
            and re-uniting a fast read to "850ms" and a slow one to "3m 5s" makes it unscannable.
            Only the NUMBER localizes — "3.4s" · "3,4s". */}
        <Readout label={t.drawer.readouts.readIn} value={fmt.formatSeconds(report.timings.totalMs)} />
        <Readout label={t.drawer.readouts.searchLanguage} value={report.textSearchConfig} />
      </div>
      {skipped.length > 0 && (
        <p className="mt-2.5 text-[12px] leading-[1.45] text-fg-muted">{t.drawer.picturesSkipped(skipped)}</p>
      )}
      {images.discardedBlocks > 0 && (
        <p className="mt-1 text-[12px] leading-[1.45] text-fg-muted">
          {t.drawer.discarded(fmt, images.discardedBlocks)}
        </p>
      )}
      {warnings.length > 0 && (
        <ul className="mt-2.5 space-y-1">
          {warnings.map((warning) => (
            <li key={warning.code} className="flex items-start gap-1.5 text-[12px] leading-[1.45] text-fg-muted">
              <TriangleAlert size={11} strokeWidth={2.2} className="mt-[3px] shrink-0 text-alert" aria-hidden />
              <span>{warning.message}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

/**
 * Where a synced document came from (PRD-25): the provider, the path, the account that read
 * it, when it changed there, and a link that opens it where it lives. The link is rendered,
 * never fetched — the platform reads a provider through its own credentials on the server and
 * nowhere else.
 */
const Origin = ({ document }: { document: KnowledgeDocument }) => {
  const { formatAgo, t: messages } = useI18n()
  const t = messages.knowledge
  const folders = messages.knowledgeFolders
  const origin = document.origin
  if (!origin) return null
  // Null, not a noun phrase. A provider name and "a connected folder" cannot share one sentence
  // in Portuguese — the first contracts its preposition ("no SharePoint") and the second carries
  // its own article ("na pasta conectada") — so the unnamed case has its own catalogue entries
  // below rather than being passed in as a fragment (PRD-27 D-21).
  const surface = origin.surface ? SURFACE_LABEL[origin.surface] : null
  return (
    <section className="border-b border-rule px-4 py-3.5">
      <h3 className="eyebrow mb-2.5 flex items-center gap-1.5">
        <ProviderMark surface={origin.surface} provider={origin.provider} size={11} />
        {folders.document.where}
      </h3>
      <div className="grid grid-cols-2 gap-x-5 gap-y-2 sm:grid-cols-3">
        <Readout label={folders.document.folder} value={<span className="break-all">{origin.path}</span>} />
        {origin.accountLabel && <Readout label={folders.document.account} value={origin.accountLabel} />}
        {origin.modifiedAt && <Readout label={folders.document.changed} value={formatAgo(origin.modifiedAt)} />}
        <Readout label={folders.document.hereSince} value={formatAgo(document.createdAt)} />
      </div>
      {origin.url && (
        <a
          href={origin.url}
          target="_blank"
          rel="noreferrer"
          className="mt-2.5 inline-flex items-center gap-1 text-[12.5px] underline decoration-transparent underline-offset-2 transition-colors duration-200 hover:decoration-current"
        >
          {surface ? folders.document.openIn(surface) : folders.document.openAtSource}
          <ExternalLink size={11} aria-hidden />
          <span className="sr-only">
            ({surface ? folders.document.opensNewTab(surface) : folders.document.opensNewTabAtSource})
          </span>
        </a>
      )}
      {origin.folderId === null && (
        <p className="mt-2 text-[12px] leading-[1.45] text-fg-subtle">{t.drawer.folderGone}</p>
      )}
    </section>
  )
}

/** Downloads the file again from its provider and reads it afresh: the synced document's "read again". */
const FetchAgainButton = ({ itemId }: { itemId: string }) => {
  const messages = useI18n().t
  const folders = messages.knowledgeFolders
  const refetch = useRefetchItem()
  return (
    <span className="flex items-center gap-2">
      <Button
        size="sm"
        variant="ghost"
        icon={<RefreshCw size={12} aria-hidden />}
        disabled={refetch.isPending}
        onClick={() =>
          refetch.mutate(itemId, {
            // The button returns to its own words once the request is on its way.
            onSuccess: () => setTimeout(() => refetch.reset(), 4_000)
          })
        }
      >
        {refetch.isPending || refetch.isSuccess ? folders.document.fetching : folders.document.fetchAgain}
      </Button>
      {refetch.isError && (
        <span role="alert" className="readout text-alert">
          {describeApiError(messages, refetch.error)}
        </span>
      )}
    </span>
  )
}

const Lineage = ({ document }: { document: KnowledgeDocument }) => {
  const fmt = useI18n()
  const messages = fmt.t
  const t = messages.knowledge
  const labels = t.drawer.lineageLabels
  return (
    <section className="border-b border-rule px-4 py-3.5">
      <h3 className="eyebrow mb-2.5">{t.drawer.lineage}</h3>
      <div className="grid grid-cols-2 gap-x-5 gap-y-2 sm:grid-cols-3">
        <Readout label={labels.status} value={t.status[document.status]} />
        <Readout label={labels.size} value={fmt.formatBytes(document.sizeBytes)} />
        <Readout label={labels.passages} value={fmt.formatCount(document.chunkCount)} />
        <Readout label={labels.type} value={shortMime(document.mimeType)} />
        {document.pageCount !== null && <Readout label={labels.pages} value={fmt.formatCount(document.pageCount)} />}
        <Readout label={labels.language} value={document.language ?? t.drawer.undetected} />
        {document.author && <Readout label={labels.author} value={document.author} />}
        {document.sourceCreatedAt && <Readout label={labels.written} value={fmt.formatAgo(document.sourceCreatedAt)} />}
        <Readout label={labels.parser} value={document.parserVersion} />
        <Readout label={labels.embedding} value={document.embeddingModel ?? '—'} />
        {/* The whole stamp, and whether it is the one now deployed: the badge is what a maintainer
          scans for after a reader upgrade (PRD-24 D-7). */}
        <Readout
          label={labels.pipeline}
          value={
            document.outdated ? (
              <span className="text-alert">
                {t.drawer.pipelineOutdated(document.pipelineVersion ?? t.drawer.unstamped)}
              </span>
            ) : (
              (document.pipelineVersion ?? '—')
            )
          }
        />
        {/* Enough of the hash to compare two uploads by eye, which is all it is ever used for
          here; the whole thing is available to anything reading the row. */}
        <Readout label={labels.contentHash} value={document.contentHash.slice(0, 10)} />
        <Readout label={labels.added} value={fmt.formatAgo(document.createdAt)} />
        <Readout
          label={labels.indexed}
          value={document.indexedAt ? fmt.formatAgo(document.indexedAt) : messages.common.never}
        />
      </div>
    </section>
  )
}

/** `application/vnd.openxmlformats-officedocument.wordprocessingml.document` is not a readout. */
const shortMime = (mime: string): string => {
  // Format abbreviations, never translated (PRD-27 D-17).
  const known: Record<string, string> = {
    'application/pdf': 'pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
    'text/markdown': 'markdown',
    'text/plain': 'text',
    'text/html': 'html',
    'application/json': 'json',
    'text/csv': 'csv'
  }
  return known[mime] ?? mime.split('/').pop() ?? mime
}

const Passages = ({ document }: { document: KnowledgeDocument }) => {
  const fmt = useI18n()
  const t = fmt.t.knowledge
  const chunks = useDocumentChunks(document.sourceId, document.id)
  const [query, setQuery] = useState('')
  const [shown, setShown] = useState(PAGE)

  const all = chunks.data?.items ?? []
  // Folded on both sides (LOC-023): the passages of a Portuguese corpus are full of diacritics,
  // and a maintainer looking for "manutencao" in them got nothing at all before this.
  const term = foldForSearch(query.trim())
  const matched = term === '' ? all : all.filter((chunk) => foldForSearch(chunk.content).includes(term))
  const visible = matched.slice(0, shown)

  return (
    <section className="px-4 py-3.5">
      <div className="mb-2.5 flex flex-wrap items-center gap-x-2.5 gap-y-2">
        <h3 className="eyebrow">{t.drawer.passagesHeading}</h3>
        <span className="readout text-fg-muted">
          {term === '' ? t.drawer.passageCount(fmt, all.length) : t.drawer.matchCount(fmt, matched.length, all.length)}
        </span>
        {all.length > 8 && (
          <div className="ml-auto w-[190px]">
            <Input
              value={query}
              onChange={(event) => {
                setQuery(event.target.value)
                setShown(PAGE)
              }}
              placeholder={t.drawer.findPlaceholder}
              aria-label={t.drawer.findLabel}
            />
          </div>
        )}
      </div>

      {chunks.isLoading && (
        <p className="flex items-center gap-2 py-3 text-[13px] text-fg-subtle">
          <Spinner size={12} /> {t.drawer.loadingPassages}
        </p>
      )}

      {chunks.isError && (
        <p role="alert" className="py-2 text-[12.5px] leading-relaxed text-alert">
          {t.drawer.passagesFailed} {describeApiError(fmt.t, chunks.error)}
        </p>
      )}

      {!chunks.isLoading && !chunks.isError && all.length === 0 && (
        <EmptyState title={t.drawer.noPassages} body={t.drawer.noPassagesBody} />
      )}

      {matched.length === 0 && all.length > 0 && <p className="py-3 text-[13px] text-fg-subtle">{t.drawer.noMatch}</p>}

      <ol className="space-y-2">
        {visible.map((chunk) => (
          <li key={chunk.id} className="rounded-tile border border-rule bg-card px-3 py-2.5">
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
              <span className="readout font-medium text-primary">#{fmt.formatCount(chunk.index + 1)}</span>
              <span className="readout text-fg-subtle">{chunk.locator}</span>
              <span className="readout ml-auto text-fg-subtle">{t.drawer.tokens(fmt, chunk.tokenCount)}</span>
            </div>
            {chunk.heading && (
              <p className="mt-1 text-[12.5px] font-medium tracking-[-0.01em] text-fg-muted">{chunk.heading}</p>
            )}
            {/* Long passages are the norm; the drawer scrolls, but a single 900-token wall
                would push every other passage out of reach of a scan. */}
            <p className="mt-1.5 line-clamp-6 whitespace-pre-wrap text-[12.5px] leading-relaxed text-fg-muted">
              {chunk.content}
            </p>
          </li>
        ))}
      </ol>

      {matched.length > visible.length && (
        <div className="mt-2.5">
          <Button size="sm" variant="ghost" onClick={() => setShown((count) => count + PAGE)}>
            {t.drawer.showMore(fmt, Math.min(PAGE, matched.length - visible.length), matched.length)}
          </Button>
        </div>
      )}

      {/* The endpoint stops at a thousand. A passage list that silently ends is a corpus a
          maintainer believes they have seen all of. */}
      {all.length === ENDPOINT_LIMIT && (
        <p className="readout mt-2.5 text-fg-subtle">{t.drawer.capped(fmt, ENDPOINT_LIMIT)}</p>
      )}
    </section>
  )
}
